import { Directive, HostListener } from '@angular/core';

const HAS_WHITESPACE = /\s/;
const ALL_WHITESPACE = /\s/g;

/**
 * Prevents typing, pasting or dropping whitespace into an `<input>`.
 *
 * Needed for `type="email"` inputs: the browser strips leading/trailing
 * spaces from `input.value`, so validators can never detect them.
 *
 * @example
 * ```html
 * <input type="email" formControlName="email" appNoSpaces />
 * ```
 */
@Directive({
  selector: '[appNoSpaces]',
  standalone: true,
})
export class NoSpacesDirective {
  /** Typing (including mobile keyboards, where keydown gives no key). */
  @HostListener('beforeinput', ['$event'])
  onBeforeInput(event: InputEvent): void {
    if (event.data && HAS_WHITESPACE.test(event.data)) {
      event.preventDefault();
      this.insertWithoutSpaces(event.data);
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    this.handleTransfer(event, event.clipboardData);
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent): void {
    this.handleTransfer(event, event.dataTransfer);
  }

  private handleTransfer(event: Event, data: DataTransfer | null): void {
    const text = data?.getData('text') ?? '';
    if (HAS_WHITESPACE.test(text)) {
      event.preventDefault();
      this.insertWithoutSpaces(text);
    }
  }

  /**
   * execCommand inserts at the cursor (selectionStart is not available on
   * type="email"), keeps undo history and fires the `input` event that
   * updates the Angular form control.
   */
  private insertWithoutSpaces(text: string): void {
    const cleaned = text.replace(ALL_WHITESPACE, '');
    if (cleaned) {
      document.execCommand('insertText', false, cleaned);
    }
  }
}
